import React, { useCallback, useMemo, useRef } from 'react'
import { FieldValues, useFormContext, useWatch } from 'react-hook-form'
import styled, { css } from 'styled-components'
import { isArray, isEqual, isNil } from 'lodash-es'
import { HStack, VStack } from '@musinsa/mwms-ds'
import Text from '@/components/content/Text'
import useClickOutside from '@/hooks/useClickOutside'
import useHorizontalScrollButtons from '@/hooks/useHorizontalScrollButtons'
import useModal from '@/hooks/useModal'
import { borderRadiusSystem, colorSystem, colors, zIndexSystem } from '@/styles/theme'
import { IconButton, PrimaryButton, Typography } from '@musinsa/layer-components-desktop'

export interface TFilterItem<T> {
  field?: keyof T | (keyof T)[]
  label?: string
  required?: boolean
  component: React.ReactNode | ((props: TFilterComponentProps) => React.ReactNode)
  fieldWidth?: number
}

export interface TFilterComponentProps {
  isOnPanel?: boolean
  label?: string
  required?: boolean
}

interface FilterListV2Props<T> {
  list: TFilterItem<T>[]
  onSearch?: () => void
  onReset?: (initValues?: FieldValues) => void
  isSubmitting?: boolean
  initialValues: T
}

function FilterListV2<T extends FieldValues>({ list, onSearch, onReset, isSubmitting = false, initialValues }: FilterListV2Props<T>) {
  const values = useWatch<T>()
  const { getValues } = useFormContext()
  const triggerContainerRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const isValidFilterValue = useCallback((value: unknown) => {
    if (isNil(value) || value === '') return false
    if (isArray(value) && value.length === 0) return false
    if (typeof value === 'object' && !isArray(value) && Object.keys(value as Record<string, unknown>).length === 0) return false
    return true
  }, [])

  const { showResetButton, selectedFilterCount, hasAllRequiredValues } = useMemo(() => {
    const currentValues = getValues()

    return list.reduce(
      (acc, { field, required }) => {
        const validFieldKeys = (Array.isArray(field) ? field : [field]).filter((fieldKey) => fieldKey && fieldKey in currentValues)
        if (validFieldKeys.length === 0) return acc

        const fieldResults = validFieldKeys.map((fieldKey) => {
          const currentValue = currentValues[fieldKey as keyof typeof currentValues]
          const initialValue = initialValues[fieldKey as keyof typeof initialValues]
          const isValidValue = isValidFilterValue(currentValue)
          const hasInitialValue = isValidFilterValue(initialValue)

          const hasValueChanged = required ? (hasInitialValue ? isValidValue : isValidValue && !isEqual(currentValue, initialValue)) : isValidValue
          return { hasValueChanged, isValidValue }
        })

        const hasAnyValueChanged = fieldResults.some((result) => result.hasValueChanged)
        const allFieldsValid = fieldResults.every((result) => result.isValidValue)

        return {
          selectedFilterCount: acc.selectedFilterCount + (hasAnyValueChanged ? 1 : 0),
          showResetButton: acc.showResetButton || hasAnyValueChanged,
          hasAllRequiredValues: acc.hasAllRequiredValues && (required ? allFieldsValid : true),
        }
      },
      { selectedFilterCount: 0, showResetButton: false, hasAllRequiredValues: true },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, initialValues, isValidFilterValue, getValues, values])

  const { scrollContainerRef, showLeftButton, showRightButton, handleScrollLeft, handleScrollRight } = useHorizontalScrollButtons()

  const { open: isPanelOpen, onCloseModal: handleClose, onToggleModal: handleToggle } = useModal()

  useClickOutside(triggerContainerRef, handleClose, isPanelOpen)

  const handleSearch = useCallback(() => {
    onSearch?.()
    handleClose()
  }, [onSearch, handleClose])

  const handleReset = useCallback(() => onReset?.(), [onReset])

  const renderPanel = () => (
    <PanelContent ref={panelRef}>
      <HStack justify="space-between" align="center" style={{ padding: '16px 20px ', borderBottom: `1px solid ${colorSystem.border.element}` }}>
        <Typography name="base-text-xl-semibold" colorToken="fg">
          필터
        </Typography>
        <IconButton icon="ic-close" size="xsmall" variant="ghost" onClick={handleClose} />
      </HStack>
      <VStack gap={20} style={{ padding: '20px' }}>
        {list?.map(({ field, label, required, component }, fieldIdx) => {
          const realField = Array.isArray(field) ? field[0] : field
          return (
            <VStack gap={8} as="div" key={`field-panel-wrap-${String(realField)}-${fieldIdx}`}>
              {label && (
                <HStack gap={2} align="center">
                  <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
                    {label}
                  </Text>
                  {required && <RequiredIndicator />}
                </HStack>
              )}
              <div key={`field-panel-item-${String(realField)}-${fieldIdx}`}>
                {typeof component === 'function' ? component({ isOnPanel: true }) : component}
              </div>
            </VStack>
          )
        })}
      </VStack>
      <HStack justify="flex-end" align="center" gap={8} style={{ padding: '14px 20px', borderTop: `1px solid ${colorSystem.border.element}` }}>
        {showResetButton && (
          <PrimaryButton variant="outlined" size="large" onClick={handleReset}>
            초기화
          </PrimaryButton>
        )}
        <PrimaryButton variant="filled" size="large" onClick={handleSearch} disabled={isSubmitting || !hasAllRequiredValues}>
          검색
        </PrimaryButton>
      </HStack>
    </PanelContent>
  )

  return (
    <form aria-label="filter-list-v2" onSubmit={(e) => e.preventDefault()}>
      <HStack gap={8} align="center">
        <FilterButtonWrapper ref={triggerContainerRef}>
          <PrimaryButton onClick={handleToggle} prefixIcon="ic-filter" size="large" variant="outlined">
            <HStack gap={4} align="center">
              <Typography name="base-text-l-medium" colorToken="fg">
                전체 필터
              </Typography>
              <span style={{ visibility: selectedFilterCount > 0 ? 'visible' : 'hidden', display: 'inline-flex' }}>
                <Typography name="base-text-l-semibold" colorToken="layerDesktop-fg-accent">
                  {selectedFilterCount > 0 ? selectedFilterCount : 0}
                </Typography>
              </span>
            </HStack>
          </PrimaryButton>

          {isPanelOpen && renderPanel()}
        </FilterButtonWrapper>

        <ScrollContainerWrapper>
          <FilterListContainer ref={scrollContainerRef}>
            {list?.map(({ field, component, fieldWidth }, fieldIdx) => (
              <FilterFieldItem $fieldWidth={fieldWidth} key={`field-horizontal-item-${String(field)}-${fieldIdx}`}>
                {typeof component === 'function' ? component({ isOnPanel: false }) : component}
              </FilterFieldItem>
            ))}
          </FilterListContainer>

          {showLeftButton && (
            <LeftScrollButtonWrapper>
              <IconButton icon="ic-chevronLeft" size="xsmall" onClick={handleScrollLeft} />
            </LeftScrollButtonWrapper>
          )}

          {showRightButton && (
            <RightScrollButtonWrapper>
              <IconButton icon="ic-chevronRight" size="xsmall" onClick={handleScrollRight} />
            </RightScrollButtonWrapper>
          )}
        </ScrollContainerWrapper>

        <HStack gap={8} align="center" style={{ flexShrink: 0 }}>
          <PrimaryButton variant="outlined" size="large" onClick={handleSearch} disabled={isSubmitting || !hasAllRequiredValues}>
            검색
          </PrimaryButton>
          {showResetButton && (
            <PrimaryButton variant="ghost" size="large" prefixIcon="ic-reset" onClick={handleReset}>
              초기화
            </PrimaryButton>
          )}
        </HStack>
      </HStack>
    </form>
  )
}

const FilterButtonWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`

const ScrollContainerWrapper = styled.div`
  position: relative;
  overflow: hidden;
`

const FilterListContainer = styled(HStack)`
  position: relative;
  padding: 8px 0;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const FilterFieldItem = styled.div<{ $fieldWidth?: number }>`
  max-width: ${({ $fieldWidth }) => ($fieldWidth ? `${$fieldWidth}px` : '100%')};
  flex-wrap: nowrap;
  text-align: left;
  flex-shrink: 0;
`

const scrollButtonStyles = css`
  position: absolute;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 100%;
  cursor: pointer;
  z-index: ${zIndexSystem.modal};
`

const LeftScrollButtonWrapper = styled.div`
  ${scrollButtonStyles};
  left: 0;
  background: linear-gradient(to left, rgba(255, 255, 255, 0), ${colors.white} 30%);
`

const RightScrollButtonWrapper = styled.div`
  ${scrollButtonStyles};
  right: 0;
  background: linear-gradient(to right, rgba(255, 255, 255, 0), ${colors.white} 30%);
`

const PanelContent = styled(VStack)`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  border: 1px solid ${colorSystem.border.element};
  border-radius: ${borderRadiusSystem.sm}px;
  width: 380px;
  max-width: 90vw;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  background: ${colors.white};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: ${zIndexSystem.modal};
`

const RequiredIndicator = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: ${colors.blue500};
`

export default FilterListV2
